import { useState, useEffect } from 'react';
import { Plus, Trash2, ExternalLink, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useStore } from '@/lib/store';
import * as api from '@/lib/api';
import type { DatabasePlaylistLink } from '@/lib/supabase';

interface ManagePlaylistLinksProps {
  playlistType: 'season' | 'round' | 'league' | 'curated' | 'personal';
  playlistId: string;
  playlistName?: string;
  onClose?: () => void;
}

const platforms = [
  { value: 'spotify', label: 'Spotify' },
  { value: 'apple_music', label: 'Apple Music' },
  { value: 'amazon_music', label: 'Amazon Music' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tidal', label: 'Tidal' },
  { value: 'deezer', label: 'Deezer' },
  { value: 'soundiiz', label: 'Soundiiz' },
] as const;

export function ManagePlaylistLinks({
  playlistType,
  playlistId,
  playlistName,
  onClose,
}: ManagePlaylistLinksProps) {
  const { toast } = useToast();
  const { state } = useStore();
  const { currentUser } = state;
  const [links, setLinks] = useState<DatabasePlaylistLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newLink, setNewLink] = useState({
    platform: 'spotify' as const,
    url: '',
    playlist_name: '',
  });

  useEffect(() => {
    fetchLinks();
  }, [playlistType, playlistId]);

  const fetchLinks = async () => {
    try {
      setIsLoading(true);
      const data = await api.playlistLinkAPI.getPlaylistLinks(playlistType, playlistId);
      setLinks(data);
    } catch (error) {
      console.error('Failed to fetch playlist links:', error);
      toast({
        title: 'Error',
        description: 'Failed to load playlist links.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddLink = async () => {
    if (!newLink.url.trim()) {
      toast({
        title: 'URL required',
        description: 'Please enter a playlist URL.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await api.playlistLinkAPI.upsertPlaylistLink({
        playlist_type: playlistType,
        playlist_id: playlistId,
        platform: newLink.platform,
        url: newLink.url.trim(),
        playlist_name: newLink.playlist_name.trim() || undefined,
        created_by: currentUser?.id,
      });

      toast({
        title: 'Success',
        description: 'Playlist link added successfully.',
      });

      setShowAddDialog(false);
      setNewLink({ platform: 'spotify', url: '', playlist_name: '' });
      fetchLinks();
    } catch (error) {
      console.error('Failed to add playlist link:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add playlist link.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteLink = async (platform: DatabasePlaylistLink['platform']) => {
    if (!confirm(`Are you sure you want to delete the ${platform} link?`)) {
      return;
    }

    try {
      await api.playlistLinkAPI.deletePlaylistLink(playlistType, playlistId, platform);
      toast({
        title: 'Success',
        description: 'Playlist link deleted successfully.',
      });
      fetchLinks();
    } catch (error) {
      console.error('Failed to delete playlist link:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete playlist link.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading playlist links...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Playlist Links</h3>
          {playlistName && (
            <p className="text-sm text-muted-foreground">{playlistName}</p>
          )}
        </div>
        <Button onClick={() => setShowAddDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Link
        </Button>
      </div>

      {links.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground border-2 border-dashed rounded-lg">
          No playlist links yet. Click "Add Link" to add one.
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((link) => {
            const platform = platforms.find((p) => p.value === link.platform);
            return (
              <div
                key={link.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex-1">
                  <div className="font-medium">{platform?.label || link.platform}</div>
                  {link.playlist_name && (
                    <div className="text-sm text-muted-foreground">{link.playlist_name}</div>
                  )}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
                  >
                    {link.url}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteLink(link.platform)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Playlist Link</DialogTitle>
            <DialogDescription>
              Add a link to this playlist on a music streaming platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <Select
                value={newLink.platform}
                onValueChange={(value) =>
                  setNewLink({ ...newLink, platform: value as typeof newLink.platform })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {platforms.map((platform) => (
                    <SelectItem key={platform.value} value={platform.value}>
                      {platform.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">Playlist URL</Label>
              <Input
                id="url"
                placeholder="https://..."
                value={newLink.url}
                onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="playlist_name">Playlist Name (Optional)</Label>
              <Input
                id="playlist_name"
                placeholder="Name of the playlist on this platform"
                value={newLink.playlist_name}
                onChange={(e) => setNewLink({ ...newLink, playlist_name: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLink}>
              <Save className="h-4 w-4 mr-2" />
              Save Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


